import React, { memo, useCallback, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Typography,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  TextField,
} from "@material-ui/core";
import {
  Settings as SettingsIcon,
  ExpandLess as CollapseIcon,
  DragIndicator as DragIcon,
} from "@material-ui/icons";
import { Droppable } from "react-beautiful-dnd";
import KanbanEnterpriseCard from "./KanbanEnterpriseCard";

const CARD_HEIGHTS = {
  rich:     { compact: 90, comfortable: 140, detailed: 195 },
  discrete: { compact: 100, comfortable: 120, detailed: 140 },
};
const COLUMN_WIDTH = 320;

const useStyles = makeStyles((theme) => ({
  column: {
    display: "flex",
    flexDirection: "column",
    minWidth: COLUMN_WIDTH,
    maxWidth: COLUMN_WIDTH,
    height: "100%",
    borderRadius: 12,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.04)"
        : "rgba(0,0,0,0.02)",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255,255,255,0.06)"
        : "1px solid rgba(0,0,0,0.04)",
    overflow: "hidden",
  },
  columnCollapsed: {
    minWidth: 48,
    maxWidth: 48,
    cursor: "pointer",
  },
  header: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1.5, 1.5, 1, 1.5),
    gap: theme.spacing(0.75),
    flexShrink: 0,
  },
  dragHandle: {
    cursor: "grab",
    color: theme.palette.text.disabled,
    display: "flex",
    alignItems: "center",
    "&:hover": { color: theme.palette.text.secondary },
    "& svg": { fontSize: 16 },
  },
  colorDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    flexShrink: 0,
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: "0.88rem",
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: theme.palette.text.primary,
    cursor: "default",
  },
  inlineInput: {
    flex: 1,
    "& input": {
      fontWeight: 600,
      fontSize: "0.88rem",
      padding: "2px 4px",
    },
  },
  countBadge: {
    height: 20,
    fontSize: "0.7rem",
    fontWeight: 600,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,0.12)"
        : "rgba(0,0,0,0.08)",
  },
  countBadgeWarning: {
    backgroundColor: "rgba(255, 152, 0, 0.25)",
    color: "#e65100",
  },
  countBadgeDanger: {
    backgroundColor: "rgba(244, 67, 54, 0.25)",
    color: "#b71c1c",
  },
  headerBtn: {
    padding: 4,
    "& svg": { fontSize: 16 },
  },
  cardList: {
    flex: 1,
    minHeight: 0,
    padding: theme.spacing(0, 1, 1, 1),
    overflowY: "auto",
    overflowX: "hidden",
    "&::-webkit-scrollbar": { width: 4 },
    "&::-webkit-scrollbar-thumb": {
      background: "rgba(128,128,128,0.3)",
      borderRadius: 4,
    },
  },
  droppableArea: {
    minHeight: 60,
  },
  emptyColumn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.disabled,
    fontSize: "0.82rem",
  },
  loadMoreBtn: {
    textAlign: "center",
    padding: theme.spacing(1),
  },
  collapsedContent: {
    writingMode: "vertical-lr",
    textOrientation: "mixed",
    transform: "rotate(180deg)",
    padding: theme.spacing(2, 0),
    fontWeight: 600,
    fontSize: "0.82rem",
    color: theme.palette.text.secondary,
    textAlign: "center",
    flex: 1,
  },
}));

const KanbanEnterpriseColumn = memo(
  ({
    column,
    tickets = [],
    count = 0,
    hasMore = false,
    loading = false,
    onLoadMore,
    onEditColumn,
    onColumnRename,
    onHoverCard,
    onLeaveCard,
    onQuickAction,
    dragHandleProps,
    isAdmin = false,
    viewMode = "comfortable",
    cardStyle = "rich",
  }) => {
    const classes = useStyles();
    const [collapsed, setCollapsed] = useState(false);
    const [editing, setEditing] = useState(false);
    const [editTitle, setEditTitle] = useState("");
    const inputRef = useRef(null);
    const listRef = useRef(null);

    // SLA / maxCards from settingsJson
    const settings = column.settingsJson || {};
    const maxCards = settings.maxCards || 0;
    const slaConfig = settings.sla || null;
    const visibleFields = settings.visibleFields || null;

    const handleScroll = useCallback(
      (e) => {
        if (!hasMore || loading) return;
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop - clientHeight < 200) {
          onLoadMore && onLoadMore(column.id);
        }
      },
      [hasMore, loading, onLoadMore, column.id]
    );

    // ── Inline edit ──
    const startEditing = useCallback(() => {
      if (!isAdmin) return;
      setEditTitle(column.name);
      setEditing(true);
      setTimeout(() => inputRef.current?.focus(), 50);
    }, [column.name, isAdmin]);

    const commitRename = useCallback(() => {
      const trimmed = editTitle.trim();
      setEditing(false);
      if (trimmed && trimmed !== column.name && onColumnRename) {
        onColumnRename(column.id, trimmed);
      }
    }, [editTitle, column.id, column.name, onColumnRename]);

    const handleKeyDown = useCallback(
      (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commitRename();
        } else if (e.key === "Escape") {
          setEditing(false);
        }
      },
      [commitRename]
    );

    // ── Count badge styling (limit indicator) ──
    const getBadgeClass = () => {
      if (!maxCards || maxCards <= 0) return classes.countBadge;
      const ratio = count / maxCards;
      if (ratio >= 1) return `${classes.countBadge} ${classes.countBadgeDanger}`;
      if (ratio >= 0.8) return `${classes.countBadge} ${classes.countBadgeWarning}`;
      return classes.countBadge;
    };

    const badgeLabel = maxCards > 0 ? `${count}/${maxCards}` : count;
    const badgeTooltip =
      maxCards > 0
        ? `${count} de ${maxCards} tickets (limite da coluna)`
        : `${count} tickets`;

    if (collapsed) {
      return (
        <div
          className={`${classes.column} ${classes.columnCollapsed}`}
          onClick={() => setCollapsed(false)}
        >
          <div className={classes.header}>
            <div
              className={classes.colorDot}
              style={{ backgroundColor: column.color }}
            />
          </div>
          <div className={classes.collapsedContent}>
            {column.name} ({count})
          </div>
        </div>
      );
    }

    return (
      <div className={classes.column}>
        {/* Header */}
        <div className={classes.header}>
          {/* Drag handle — admin only */}
          {isAdmin && dragHandleProps && (
            <Tooltip title="Arrastar coluna">
              <span className={classes.dragHandle} {...dragHandleProps}>
                <DragIcon />
              </span>
            </Tooltip>
          )}

          <div
            className={classes.colorDot}
            style={{ backgroundColor: column.color || "#757575" }}
          />

          {/* Título: inline edit ou texto */}
          {editing ? (
            <TextField
              inputRef={inputRef}
              className={classes.inlineInput}
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              onBlur={commitRename}
              onKeyDown={handleKeyDown}
              size="small"
              variant="standard"
              autoFocus
            />
          ) : (
            <Typography
              className={classes.headerTitle}
              onDoubleClick={startEditing}
              title={isAdmin ? "Duplo-clique para editar" : column.name}
            >
              {column.name}
            </Typography>
          )}

          <Tooltip title={badgeTooltip}>
            <Chip
              className={getBadgeClass()}
              label={badgeLabel}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Editar coluna">
            <IconButton
              className={classes.headerBtn}
              onClick={() => onEditColumn && onEditColumn(column)}
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Recolher">
            <IconButton
              className={classes.headerBtn}
              onClick={() => setCollapsed(true)}
            >
              <CollapseIcon />
            </IconButton>
          </Tooltip>
        </div>

        {/* Cards */}
        <Droppable droppableId={String(column.id)} type="CARD">
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={classes.cardList}
              onScroll={handleScroll}
              style={{
                backgroundColor: snapshot.isDraggingOver
                  ? "rgba(33,150,243,0.06)"
                  : undefined,
              }}
            >
              {tickets.length === 0 && !loading && (
                <div className={classes.emptyColumn}>
                  Nenhum ticket
                </div>
              )}
              {tickets.map((ticket, index) => (
                <KanbanEnterpriseCard
                  key={ticket.id}
                  ticket={ticket}
                  index={index}
                  onHover={onHoverCard}
                  onLeave={onLeaveCard}
                  viewMode={viewMode}
                  cardStyle={cardStyle}
                  slaConfig={slaConfig}
                  visibleFields={visibleFields}
                  onQuickAction={onQuickAction}
                  isAdmin={isAdmin}
                />
              ))}
              {provided.placeholder}
              {loading && (
                <div className={classes.loadMoreBtn}>
                  <CircularProgress size={20} />
                </div>
              )}
            </div>
          )}
        </Droppable>
      </div>
    );
  }
);

KanbanEnterpriseColumn.displayName = "KanbanEnterpriseColumn";

export default KanbanEnterpriseColumn;
